import React from 'react';
import { cn } from '~/lib/utils';
import styles from './UserBubble.module.css';
import type { BubbleKind, ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface UserBubbleProps {
  /**
   * `sent` is the committed message; `queued` is one typed while a run was still writing and is
   * waiting its turn. Queued swaps the ground to light-4, adds the dashed hairline and drops the
   * body to `--cv15-text-muted`.
   */
  kind?: BubbleKind;
  /**
   * In-place edit — the bubble becomes the field, there is no modal. Orthogonal to `kind`: a
   * queued message and a sent message can both be under the caret, and both get the `Editing`
   * board's ring and caret (DECISIONS.md item 5, and its amendment). What that board actually fixes
   * is that the body must not move when the caret arrives, so the ring is drawn inside the resting
   * inset: a `sent` bubble has no stroke at rest and gives back the 1px of padding for it
   * (10/12 + 1px); a `queued` bubble already carries a 1px hairline and simply swaps dashed for
   * solid (11/13 + 1px). Same rule, two resting boxes. `UserBubble.module.css`'s `.editing` block carries the derivation.
   */
  editing?: boolean;
  /**
   * Hard 168px clamp behind a 44px fade. Independent of content length — 438 characters and 724
   * characters clamp to the identical box.
   */
  clamped?: boolean;
  /**
   * Wrapping flow instead of a text block, for messages carrying inline `MentionChip`s. The
   * mentions stay in the sentence; they never become a separate row.
   */
  inline?: boolean;
  /**
   * The message. In `editing` this is the text run plus a `Caret`; in `inline` it is a mix of
   * `MentionChip`s and prose.
   */
  children: React.ReactNode;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The user's message bubble — one 320px-capped, content-hugging box that every column in the design
 * draws, in four combinable states.
 *
 * What it is **not**: the clamp's `Show more` footer, the hover copy/edit actions, the queue's
 * `Queued` meta line and the attachment row above it are all siblings that `UserMessage` composes
 * around it, exactly as Paper draws them — outside the bubble, 4px below or 8px above.
 *
 * The ground is `--cv15-bubble` (surface-3) in every state including hover; hover changes nothing
 * here at all.
 */
export const UserBubble = React.forwardRef<HTMLDivElement, UserBubbleProps>(function UserBubble(
  {
    kind = 'sent',
    editing = false,
    clamped = false,
    inline = false,
    children,
    theme: themeProp,
    className,
  },
  ref,
) {
  const theme = useV15Theme(themeProp);
  return (
    <div
      ref={ref}
      className={cn(
        chatThemeClass(theme),
        styles.bubble,
        kind === 'queued' && styles.queued,
        editing && styles.editing,
        inline && styles.inline,
        clamped && styles.clamped,
        className,
      )}
      // Deliberately no `aria-expanded` / `aria-hidden` on the clamp: the clipped text is still in
      // the DOM and still read, which is the behaviour we want — the clamp is a reading convenience
      // for the eye, not a redaction. The control that toggles it lives in `BubbleFooter`, and that
      // is a real button that carries the state.
    >
      {children}
    </div>
  );
});
